import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import posthog from 'posthog-js';
import HowItWorks from '../components/landing/HowItWorks';
import SocialProof from '../components/landing/SocialProof';
import Features from '../components/landing/Features';
import BuiltForPDR from '../components/landing/BuiltForPDR';
import Pricing from '../components/landing/Pricing';
import FAQ from '../components/landing/FAQ';
import Footer from '../components/landing/Footer';
import { PRICING } from '../constants/pricing';

const s = {
  page: {
    minHeight: '100vh',
    backgroundColor: 'var(--paper)',
    fontFamily: 'var(--font-sans)',
  },
  nav: {
    position: 'sticky',
    top: 0,
    zIndex: 100,
    backgroundColor: 'var(--paper)',
    borderBottom: '0.5px solid rgba(0,0,0,0.12)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '20px 24px',
  },
  logo: {
    fontFamily: 'var(--font-serif)',
    fontSize: '24px',
    color: 'var(--ink)',
    textDecoration: 'none',
  },
  logoAccent: {
    color: 'var(--signal)',
  },
  signIn: {
    fontSize: '14px',
    color: 'var(--slate)',
    textDecoration: 'none',
    whiteSpace: 'nowrap',
  },
  // Groups the two right-hand nav items so the nav's space-between still
  // resolves to logo-left / actions-right rather than spreading three children
  // evenly across the bar.
  navActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    flexShrink: 0,
  },
  // Nav-scale version of heroCta: same custom properties, radius, family and
  // weight, one step down in font size and horizontal padding. Height comes
  // from minHeight + inline-flex centering rather than vertical padding, so the
  // 44px tap target holds regardless of how the font metrics resolve.
  navCta: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '44px',
    padding: '0 20px',
    backgroundColor: 'var(--signal)',
    color: 'var(--on-signal)',
    border: 'none',
    borderRadius: '8px',
    fontFamily: 'var(--font-sans)',
    fontSize: '15px',
    fontWeight: 500,
    cursor: 'pointer',
    textAlign: 'center',
    textDecoration: 'none',
    whiteSpace: 'nowrap',
  },
  center: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '0 16px',
  },
  hero: {
    paddingTop: '40px',
    paddingBottom: '24px',
    maxWidth: '1100px',
    width: '100%',
  },
  headline: {
    fontFamily: 'var(--font-serif)',
    fontSize: '48px',
    color: 'var(--ink)',
    lineHeight: 1.1,
    margin: '0 0 16px 0',
  },
  eyebrow: {
    fontFamily: 'var(--font-sans)',
    fontSize: '13px',
    fontWeight: 500,
    color: 'var(--signal)',
    textTransform: 'uppercase',
    letterSpacing: '1.5px',
    margin: '0 0 16px 0',
  },
  heroInner: {
    display: 'flex',
    gap: '48px',
    // Top-aligned, not centred: the phone mockup is roughly twice the height of
    // the text column, so centring split the difference into ~170px of dead
    // space above the eyebrow. The stacked layout re-centres this at ≤640px.
    alignItems: 'flex-start',
    width: '100%',
  },
  heroText: {
    flex: 1,
    textAlign: 'left',
    minWidth: 0,
  },
  heroVisual: {
    flex: 1,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minWidth: 0,
  },
  // The device chrome below is drawn entirely in CSS — the image file carries
  // no bezel. Titanium greys are raw literals because the token set has no
  // metal ramp: --ink is a text colour and reads flat here. Kept local to the
  // frame rather than promoted to :root, since nothing else needs them.
  phoneFrame: {
    position: 'relative',
    display: 'block',
    width: '100%',
    maxWidth: '320px',
    // Thicker top and bottom than the sides. A uniform bezel is what made the
    // old frame read as a rounded box holding a second rounded box.
    padding: '11px 8px',
    // Outer radius = inner radius + side bezel (36 + 8), so the two curves are
    // concentric on the left and right edges instead of unrelated roundings.
    borderRadius: '44px',
    background: 'linear-gradient(180deg, #45484d 0%, #26292e 16%, #1c1f24 50%, #292c31 84%, #3f4247 100%)',
    // Unchanged. --sh-raise is the nearest token but tops out at 8% opacity
    // over 28px — calibrated for cards resting on a page, not a device
    // floating above it. Swapping it in would flatten the frame, so the
    // literal stays.
    boxShadow: '0 20px 50px rgba(0,0,0,0.18)',
  },
  phoneScreen: {
    position: 'relative',
    width: '100%',
    borderRadius: '36px',
    overflow: 'hidden',
    backgroundColor: 'var(--card)',
    // 1px highlight where bezel meets glass. Drawn outward from the screen so
    // it lands on the bezel rather than over the screenshot; an element's own
    // overflow:hidden does not clip its box-shadow.
    boxShadow: '0 0 0 1px rgba(255,255,255,0.12)',
  },
  phoneImage: {
    display: 'block',
    width: '100%',
    height: 'auto',
  },
  // Flush to the frame edge and protruding 2px. Absolutely positioned, so they
  // add nothing to layout width — see the overflow note in the ≤640px block.
  sideButton: {
    position: 'absolute',
    width: '3px',
    background: 'linear-gradient(180deg, #4e5158 0%, #383b41 50%, #4e5158 100%)',
  },
  // Sits above the image in document order, so it pushes the screenshot down
  // rather than covering it.
  statusBar: {
    position: 'relative',
    height: '38px',
    // The screenshot's own top strip is the app's sticky header, which is
    // var(--card) — see Dashboard.js StickyHeader — so this reads as continuous
    // with the image rather than as a band stuck on top of it.
    backgroundColor: 'var(--card)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0 20px',
    boxSizing: 'border-box',
  },
  statusTime: {
    fontFamily: 'var(--font-sans)',
    fontSize: '11.5px',
    fontWeight: 600,
    color: 'var(--ink)',
    letterSpacing: '0.2px',
  },
  statusGlyphs: {
    display: 'flex',
    alignItems: 'center',
    gap: '5px',
  },
  // Centred against the strip itself rather than sitting between the time and
  // the glyphs, so its vertical centre lines up with both regardless of how
  // wide either side renders.
  dynamicIsland: {
    position: 'absolute',
    left: '50%',
    top: '50%',
    transform: 'translate(-50%, -50%)',
    width: '26%',
    aspectRatio: '3.5',
    backgroundColor: '#000000',
    borderRadius: '999px',
  },
  homeIndicator: {
    position: 'absolute',
    left: '50%',
    bottom: '8px',
    transform: 'translateX(-50%)',
    width: '34%',
    height: '4px',
    borderRadius: '999px',
    // Dark, not light: the screenshot's bottom edge is the app's white tab bar,
    // where a light bar would be invisible. iOS inverts this indicator against
    // light content for the same reason.
    backgroundColor: 'rgba(20,22,26,0.30)',
  },
  heroSubhead: {
    fontFamily: 'var(--font-sans)',
    fontSize: '17px',
    color: 'var(--slate)',
    lineHeight: 1.5,
    margin: '16px 0 28px 0',
    maxWidth: '460px',
  },
  // Now an <a>, so it carries textDecoration: 'none', and inline-flex centring
  // replaces the vertical text centring a <button> did on its own. Padding,
  // font and colours are unchanged, so the rendered box is the same size.
  heroCta: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '14px 28px',
    backgroundColor: 'var(--signal)',
    color: 'var(--on-signal)',
    border: 'none',
    borderRadius: '8px',
    fontFamily: 'var(--font-sans)',
    fontSize: '16px',
    fontWeight: 500,
    cursor: 'pointer',
    textAlign: 'center',
    textDecoration: 'none',
  },
  heroPriceNote: {
    fontFamily: 'var(--font-sans)',
    fontSize: '14px',
    color: 'var(--mute)',
    margin: '14px 0 0 0',
  },
};

function LandingPage() {
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    posthog.capture('landing_page_viewed', {
      referrer: document.referrer || '',
      utm_source: params.get('utm_source') || '',
      utm_medium: params.get('utm_medium') || '',
      utm_campaign: params.get('utm_campaign') || '',
    });
  }, []);

  return (
    <div style={s.page}>
      {/* Nav */}
      <nav className="lp-nav" style={s.nav}>
        <span style={s.logo}>
          Tru<span style={s.logoAccent}>Flow</span>
        </span>
        <div className="lp-nav-actions" style={s.navActions}>
          <Link
            to="/login"
            className="lp-nav-signin"
            style={s.signIn}
            onMouseEnter={e => { e.target.style.color = 'var(--signal)'; }}
            onMouseLeave={e => { e.target.style.color = 'var(--slate)'; }}
          >
            Sign in
          </Link>
          <Link to="/signup" className="lp-nav-cta" style={s.navCta}>
            Get started
          </Link>
        </div>
      </nav>

      <div style={s.center}>
        {/* Hero */}
        <div style={s.hero}>
          <div className="lp-hero-inner" style={s.heroInner}>
            <div style={s.heroText}>
              <p style={s.eyebrow}>Built for PDR techs.</p>
              <h1 className="lp-headline" style={s.headline}>Know your numbers.</h1>
              <p style={s.heroSubhead}>The financial app built for paintless dent repair. Calculate your tax reserve, see slow months coming, and never get blindsided by a quarterly deadline.</p>
              <Link to="/signup" style={s.heroCta}>
                Get started
              </Link>
              <p style={s.heroPriceNote}>
                {`$${PRICING.monthly.intro}/month for your first ${PRICING.monthly.introMonths} months, then $${PRICING.monthly.base}/month.`}
              </p>
            </div>
            <div style={s.heroVisual}>
              <div style={s.phoneFrame}>
                {/* Silent switch, then volume up / down */}
                <div style={{ ...s.sideButton, left: '-2px', top: '17%', height: '3.6%', borderRadius: '2px 1px 1px 2px' }} />
                <div style={{ ...s.sideButton, left: '-2px', top: '24%', height: '6.4%', borderRadius: '2px 1px 1px 2px' }} />
                <div style={{ ...s.sideButton, left: '-2px', top: '32%', height: '6.4%', borderRadius: '2px 1px 1px 2px' }} />
                {/* Power, roughly a third down the right edge */}
                <div style={{ ...s.sideButton, right: '-2px', top: '31%', height: '9%', borderRadius: '1px 2px 2px 1px' }} />

                <div style={s.phoneScreen}>
                  <div style={s.statusBar}>
                    <span style={s.statusTime}>9:41</span>
                    <div style={s.dynamicIsland} />
                    <span style={s.statusGlyphs}>
                      <svg width="14" height="11" viewBox="0 0 16 12" fill="none" aria-hidden="true"
                           style={{ stroke: 'var(--mute)', display: 'block' }}
                           strokeWidth="1.4" strokeLinecap="round">
                        <path d="M1.4 4.1a9.6 9.6 0 0 1 13.2 0" />
                        <path d="M4.1 6.9a5.8 5.8 0 0 1 7.8 0" />
                        <path d="M6.7 9.6a2.1 2.1 0 0 1 2.6 0" />
                      </svg>
                      <svg width="21" height="10" viewBox="0 0 26 12" fill="none" aria-hidden="true"
                           style={{ display: 'block' }}>
                        <rect x="0.7" y="0.7" width="20.6" height="10.6" rx="3.2"
                              style={{ stroke: 'var(--mute)' }} strokeWidth="1.2" />
                        <rect x="2.6" y="2.6" width="13" height="6.8" rx="1.8" style={{ fill: 'var(--mute)' }} />
                        <rect x="23.2" y="4.1" width="2.2" height="3.8" rx="1.1" style={{ fill: 'var(--mute)' }} />
                      </svg>
                    </span>
                  </div>

                  <img
                    src="/dashboard-hero.jpg"
                    alt="TruFlow dashboard showing tax reserve and quarterly progress"
                    style={s.phoneImage}
                  />

                  <div style={s.homeIndicator} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <SocialProof />
      <HowItWorks />
      <Features />
      <BuiltForPDR />
      <Pricing />
      <FAQ />
      <Footer />

      <style>{`
        @media (max-width: 640px) {
          /* Nav has to stay on one line down to 320px. Tighten the bar's own
             padding, the gap, and the button's horizontal padding and font —
             never minHeight, which is what keeps the 44px tap target. */
          .lp-nav { padding: 14px 16px !important; }
          .lp-nav-actions { gap: 12px !important; }
          .lp-nav-signin { font-size: 13px !important; }
          .lp-nav-cta { padding: 0 14px !important; font-size: 14px !important; }
          .lp-headline { font-size: 32px !important; }
          /* align-items is flex-start on desktop to kill the dead space above
             the eyebrow; stacked, that would left-align the columns, so the
             centring the mobile layout already had is restored explicitly. */
          .lp-hero-inner { flex-direction: column !important; gap: 32px !important; text-align: center !important; align-items: center !important; }
          .lp-hero-inner > div { text-align: center !important; }
          .lp-hero-inner h1 { text-align: center !important; }
        }
      `}</style>
    </div>
  );
}

export default LandingPage;
